/**
 * Custom hook for managing push notifications
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../core/contexts/AuthContext';
import {
  initializePushNotifications,
  cleanupPushNotifications,
  getNotificationPermissionStatus,
  requestNotificationPermission,
  testPushNotification
} from '../services/firebase/pushNotificationService';

/**
 * Hook to manage push notifications
 */
const usePushNotifications = () => {
  const { currentUser } = useAuth();
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update permission status
  useEffect(() => {
    const updatePermissionStatus = () => {
      setPermissionStatus(getNotificationPermissionStatus());
    };

    updatePermissionStatus();

    // Listen for permission changes
    const interval = setInterval(updatePermissionStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  // Initialize push notifications when user logs in
  useEffect(() => {
    if (currentUser && !isInitialized && permissionStatus === 'granted') {
      initializeForUser();
    }
  }, [currentUser, permissionStatus]);

  // Cleanup when user logs out
  useEffect(() => {
    if (!currentUser && isInitialized) {
      cleanup();
    }
  }, [currentUser]);

  /**
   * Initialize push notifications for current user
   */
  const initializeForUser = async () => {
    if (!currentUser) return;

    setIsLoading(true);
    setError(null);

    try {
      const success = await initializePushNotifications(currentUser.uid);
      setIsInitialized(success);
      
      if (!success) {
        setError('Failed to initialize push notifications');
      }
    } catch (err) {
      setError(err.message);
      setIsInitialized(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Request notification permission
   */
  const requestPermission = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const granted = await requestNotificationPermission();
      
      if (granted && currentUser) {
        await initializeForUser();
      }
      
      return granted;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cleanup push notifications
   */
  const cleanup = async () => {
    if (!currentUser) return;

    try {
      await cleanupPushNotifications(currentUser.uid);
      setIsInitialized(false);
    } catch (err) {
      console.error('Error cleaning up push notifications:', err);
    }
  };

  /**
   * Test push notification
   */
  const sendTestNotification = () => {
    testPushNotification();
  };

  return {
    permissionStatus,
    isInitialized,
    isLoading,
    error,
    requestPermission,
    sendTestNotification,
    cleanup
  };
};

export default usePushNotifications;